import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MovieIcon from '@mui/icons-material/Movie';
import PublicIcon from '@mui/icons-material/Public';
import RadioIcon from '@mui/icons-material/Radio';
import StopIcon from '@mui/icons-material/Stop';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { channelLogoUrl, imageUrl } from '../../api/client';
import type { ActivityDelivery, ActivityEntry, ActivityState, ActivityStream } from '../../api/types';
import DecisionReasons from '../DecisionReasons';
import { episodeCode } from '../media/playbackTime';
import { containerName, describeStream, formatBitrate, formatClock } from '../player/streamText';
import { describeTarget, isChanged, METHOD_COLOR, summaryKeys } from './delivery';

const STATE_COLOR: Record<ActivityState, 'success' | 'default' | 'warning' | 'error'> = {
  Playing: 'success',
  Paused: 'default',
  Idle: 'warning',
  Stopped: 'default',
  Failed: 'error',
};

/// One thing being watched, as the person running the server wants to see it: what it is, who
/// is watching it on what, how far along they are, and how it gets to them - with the decision
/// behind that a tap away, because every delivery here explains itself.
export default function ActivityCard({
  entry,
  now,
  onStop,
}: {
  entry: ActivityEntry;
  /// The moment the page last looked, in milliseconds, for where a programme is.
  now: number;
  onStop: () => void;
}) {
  const { t } = useTranslation();
  const [whyOpen, setWhyOpen] = useState(false);
  const shared = (entry.channel?.viewers ?? 1) > 1;

  return (
    <Card variant="outlined">
      <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, p: { xs: 1.5, sm: 2 } }}>
        <Artwork entry={entry} />

        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" component="h3" sx={{ lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                {titleOf(entry)}
              </Typography>
              <Subtitle entry={entry} />
            </Box>

            {/* A shared channel stops for this viewer only; the others keep their picture. */}
            <Tooltip title={shared ? t('sessions.stopViewer') : ''}>
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={<StopIcon />}
                onClick={onStop}
                sx={{ flexShrink: 0, minHeight: 40 }}
              >
                {t('sessions.stop')}
              </Button>
            </Tooltip>
          </Stack>

          <Viewer entry={entry} />

          {entry.kind === 'Item' ? <ItemProgress entry={entry} /> : <ChannelProgress entry={entry} now={now} />}
        </Stack>
      </Box>

      {entry.error && (
        <Alert severity="error" sx={{ mx: { xs: 1.5, sm: 2 }, mb: 2 }}>
          {entry.error.message} {entry.error.remedy}
        </Alert>
      )}

      {entry.delivery && (
        <>
          <Divider />
          <Delivery delivery={entry.delivery} />

          <Box sx={{ px: { xs: 1, sm: 1.5 }, pb: 1 }}>
            <Button
              size="small"
              onClick={() => setWhyOpen((open) => !open)}
              aria-expanded={whyOpen}
              endIcon={
                <ExpandMoreIcon sx={{ transform: whyOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
              }
              sx={{ minHeight: 40 }}
            >
              {t('sessions.why')}
            </Button>
          </Box>

          <Collapse in={whyOpen} unmountOnExit>
            <Reasons delivery={entry.delivery} />
          </Collapse>
        </>
      )}
    </Card>
  );
}

/// An episode is known by its programme; the episode's own title goes underneath.
function titleOf(entry: ActivityEntry): string {
  if (entry.channel) {
    return entry.channel.name;
  }

  const item = entry.item!;

  return item.itemKind === 'Episode' && item.seriesTitle ? item.seriesTitle : item.title;
}

function Subtitle({ entry }: { entry: ActivityEntry }) {
  const { t } = useTranslation();
  let parts: (string | null | undefined)[];

  if (entry.channel) {
    const { number, programme } = entry.channel;
    parts = [number, programme ? t('sessions.onNow', { title: programme.title }) : t('sessions.noProgramme')];

    if (programme?.episodeTitle) {
      parts.push(programme.episodeTitle);
    }
  } else {
    const item = entry.item!;
    const code = item.seasonNumber != null && item.episodeNumber != null
      ? episodeCode(item.seasonNumber, item.episodeNumber)
      : undefined;

    parts = item.itemKind === 'Episode'
      ? [code, item.seriesTitle ? item.title : undefined]
      : [item.year?.toString()];

    // The file only matters where there is more than one to choose from - or no item at all.
    if (item.versionName && (item.versionCount > 1 || !item.id)) {
      parts.push(t('sessions.version', { name: item.versionName }));
    }
  }

  const text = parts.filter(Boolean).join(' · ');

  return text ? (
    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
      {text}
    </Typography>
  ) : null;
}

/// The poster, or the channel's logo, at the start of the card.
function Artwork({ entry }: { entry: ActivityEntry }) {
  const size = { xs: 56, sm: 88 };
  const [failed, setFailed] = useState(false);
  const theme = useTheme();

  let source: string | undefined;

  if (entry.item) {
    // An episode shows its programme's poster: the same size and shape as a film, and it says
    // which programme at a glance where an episode still would not.
    const poster = entry.item.itemKind === 'Episode'
      ? entry.item.seriesImage ?? entry.item.primaryImage
      : entry.item.primaryImage;

    source = imageUrl(poster);
  } else if (entry.channel?.logo) {
    source = channelLogoUrl(entry.channel.logo, theme.palette.mode);
  }

  const isChannel = entry.kind === 'Channel';

  const frame = {
    width: size,
    flexShrink: 0,
    aspectRatio: isChannel ? '1' : '2 / 3',
    borderRadius: 1,
    bgcolor: 'action.hover',
    alignSelf: 'flex-start',
  } as const;

  if (source && !failed) {
    return (
      <Box
        component="img"
        src={source}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        sx={{ ...frame, objectFit: isChannel ? 'contain' : 'cover', p: isChannel ? 1 : 0, boxSizing: 'border-box' }}
      />
    );
  }

  const Icon = isChannel ? (entry.channel?.channelKind === 'Radio' ? RadioIcon : LiveTvIcon) : MovieIcon;

  return (
    <Box sx={{ ...frame, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
      <Icon />
    </Box>
  );
}

/// Who, on what, and from where.
function Viewer({ entry }: { entry: ActivityEntry }) {
  const { t, i18n } = useTranslation();
  const name = entry.user.name ?? t('sessions.unknownUser');
  const client = [entry.client.name, entry.client.version].filter(Boolean).join(' ')
    || entry.client.deviceId
    || t('sessions.unknownClient');

  const since = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' })
    .format(new Date(entry.startedAt));

  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Chip
        size="small"
        avatar={<Avatar>{name.charAt(0).toUpperCase()}</Avatar>}
        label={name}
      />

      <Chip size="small" variant="outlined" label={client} title={entry.client.deviceId ?? undefined} />

      {entry.client.isLocalNetwork != null && (
        <Chip
          size="small"
          variant="outlined"
          color={entry.client.isLocalNetwork ? 'default' : 'info'}
          icon={entry.client.isLocalNetwork ? <HomeIcon /> : <PublicIcon />}
          label={[
            entry.client.isLocalNetwork ? t('sessions.local') : t('sessions.remote'),
            entry.client.remoteAddress,
          ].filter(Boolean).join(' · ')}
        />
      )}

      <Chip size="small" color={STATE_COLOR[entry.state]} label={t(`activityState.${entry.state}`)} />

      <Typography variant="caption" color="text.secondary">
        {t('sessions.startedAt', { time: since })}
      </Typography>
    </Stack>
  );
}

/// Where in the film somebody is, and how far ahead of them the worker has got.
function ItemProgress({ entry }: { entry: ActivityEntry }) {
  const { t } = useTranslation();
  const progress = entry.progress;

  if (!progress) {
    return null;
  }

  const total = progress.durationSeconds;
  const percent = (seconds: number) => (total ? Math.min((seconds / total) * 100, 100) : 0);
  const reported = progress.positionSource === 'Reported';

  return (
    <Box>
      {total ? (
        <Tooltip
          title={progress.transcodedToSeconds != null
            ? t('sessions.transcodedTo', { time: formatClock(progress.transcodedToSeconds) })
            : ''}
        >
          <LinearProgress
            variant={progress.transcodedToSeconds != null ? 'buffer' : 'determinate'}
            value={percent(progress.positionSeconds)}
            valueBuffer={progress.transcodedToSeconds != null ? percent(progress.transcodedToSeconds) : undefined}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Tooltip>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 0.5, justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          color="text.secondary"
          title={reported ? undefined : t('sessions.positionNotReported')}
        >
          {reported ? '' : '≈ '}
          {formatClock(progress.positionSeconds)}
          {total ? ` / ${formatClock(total)}` : ''}
        </Typography>
      </Stack>
    </Box>
  );
}

/// Television has no position; what it has is a programme, and people sharing it.
function ChannelProgress({ entry, now }: { entry: ActivityEntry; now: number }) {
  const { t, i18n } = useTranslation();
  const channel = entry.channel;

  if (!channel) {
    return null;
  }

  const programme = channel.programme;
  const clock = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' });

  let fraction = 0;

  if (programme) {
    const start = new Date(programme.startsAt).getTime();
    const end = new Date(programme.endsAt).getTime();
    fraction = end > start ? Math.min(Math.max((now - start) / (end - start), 0), 1) : 0;
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', mb: programme ? 1 : 0 }}>
        <Chip size="small" color="error" label={t('sessions.liveBadge')} />
        {channel.viewers > 1 && (
          <Chip size="small" variant="outlined" label={t('sessions.sharedWith', { count: channel.viewers - 1 })} />
        )}
      </Stack>

      {programme && (
        <>
          <LinearProgress
            variant="determinate"
            value={fraction * 100}
            color="secondary"
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Stack direction="row" sx={{ mt: 0.5, justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {clock.format(new Date(programme.startsAt))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {clock.format(new Date(programme.endsAt))}
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}

/// How it gets there: the method, what is changed, source beside target, and the hardware.
function Delivery({ delivery }: { delivery: ActivityDelivery }) {
  const { t } = useTranslation();
  const summary = delivery.method === 'DirectPlay'
    ? [t('sessions.summary.direct')]
    : summaryKeys(delivery).map((key) => t(key));

  const sourceContainer = containerName(delivery.sourceContainer);
  const targetContainer = containerName(delivery.container);

  return (
    <Stack spacing={1.25} sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 0.5 }}>
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          color={METHOD_COLOR[delivery.method]}
          label={t(`sessions.delivery.${delivery.method}`)}
          sx={{ fontWeight: 600 }}
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {summary.join(' · ')}
        </Typography>
      </Stack>

      <StreamLine
        label={t('sessions.container')}
        source={sourceContainer}
        target={targetContainer !== sourceContainer ? targetContainer : undefined}
      />

      {delivery.streams
        .filter((stream) => stream.action !== 'Discard' && stream.action !== 'Drop')
        .map((stream) => (
          <StreamLine
            key={stream.index}
            label={t(`streamKind.${stream.kind}`)}
            source={stream.source ? describeStream(stream.source) : stream.targetCodec}
            target={isChanged(stream) ? describeTarget(stream, delivery) : undefined}
            action={<ActionChip stream={stream} />}
          />
        ))}

      {delivery.encoder && (
        <StreamLine
          label={t('sessions.encoder')}
          source={[delivery.encoder.name, delivery.encoder.deviceName ?? delivery.encoder.deviceId].join(' · ')}
          action={
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {delivery.transcode?.speed != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  color={delivery.transcode.speed < 1 ? 'error' : 'default'}
                  label={t('sessions.speedValue', { speed: delivery.transcode.speed.toFixed(1) })}
                />
              )}
              {delivery.transcode?.framesPerSecond != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('sessions.framesPerSecond', { fps: Math.round(delivery.transcode.framesPerSecond) })}
                />
              )}
              {delivery.hardwareDecode === false && (
                <Chip size="small" variant="outlined" color="warning" label={t('sessions.cpuDecode')} />
              )}
            </Stack>
          }
        />
      )}

      <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
        {(delivery.transcode?.outputBitrate ?? delivery.estimatedBitrate) != null && (
          <Chip
            size="small"
            variant="outlined"
            label={formatBitrate((delivery.transcode?.outputBitrate ?? delivery.estimatedBitrate)!)}
          />
        )}
        {delivery.toneMap !== 'NotRequired' && (
          <Chip
            size="small"
            variant="outlined"
            label={[t(`toneMapStrategy.${delivery.toneMap}`), delivery.toneMapper].filter(Boolean).join(' · ')}
          />
        )}
        {delivery.deinterlace !== 'NotRequired' && (
          <Chip
            size="small"
            variant="outlined"
            label={`${t(`deinterlaceStrategy.${delivery.deinterlace}`)} · ${t(`deinterlaceMode.${delivery.deinterlaceMode}`)}`}
          />
        )}
      </Stack>
    </Stack>
  );
}

function ActionChip({ stream }: { stream: ActivityStream }) {
  const { t } = useTranslation();
  const changed = isChanged(stream);

  return (
    <Chip
      size="small"
      variant={changed ? 'filled' : 'outlined'}
      color={changed ? 'warning' : 'default'}
      label={t(`sessions.action.${stream.action}`)}
    />
  );
}

/// One stream as "source → target", the arrow pointing the way the language reads.
function StreamLine({
  label,
  source,
  target,
  action,
}: {
  label: string;
  source: string;
  target?: string;
  action?: ReactNode;
}) {
  const { direction } = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '88px 1fr' },
        columnGap: 1.5,
        rowGap: 0.25,
        alignItems: 'baseline',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>

      <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {source}
        </Typography>

        {target && (
          <>
            <ArrowForwardIcon
              fontSize="inherit"
              sx={{ color: 'text.secondary', transform: direction === 'rtl' ? 'scaleX(-1)' : 'none' }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {target}
            </Typography>
          </>
        )}

        {action}
      </Stack>
    </Box>
  );
}

/// Every check that ran, in the order it ran - with the code and the stream named under each,
/// which is what an administrator looking at somebody else's session searches by.
function Reasons({ delivery }: { delivery: ActivityDelivery }) {
  return (
    <Stack spacing={1} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
      <DecisionReasons reasons={delivery.reasons} detailed />
    </Stack>
  );
}
